import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Button, Card, Flex, Form, Input, Select, Typography, message } from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import { showConfirmModal } from "components/modals/confirmModal";
import {
  SetStaffRolesBody,
  StaffAdminTier,
  useSetStaffRoles,
} from "hooks/mutations/user";
import { removalCountLabels, staffDeactivationCheckOptions } from "hooks/queries/user";
import { ToggleRow, optionBlockStyle } from "pages/settings/settingsRows";
import { useEffect } from "react";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { User } from "shared/types";
import { ResponseHandler } from "shared/types/axios";
import { UserType } from "shared/types/enum";
import { getApiErrorMessage } from "shared/utils";
import { hasRole, isManager } from "shared/utils/role";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// System-admin-only role editor on a staff member's Profil tab. It writes the
// FULL desired state through PUT /v1/admin/staff/:id/roles (instructor yes/no
// plus one admin tier), never a raw roles array: Manager is additive and only
// the server produces ["admin", "manager"]. The studio's own path for the
// harmless direction (an admin who also teaches) is Tambah Instruktur; this
// card is where the escalating direction and instructor revoke live.
const tierOf = (roles: string[]): StaffAdminTier =>
  isManager(roles) ? "manager" : hasRole(roles, UserType.Admin) ? "admin" : "none";

const tierLabel = (tier: StaffAdminTier) => translate(`staff_roles.tier_${tier}`);

const StaffRolesCard = ({ user }: { user: User }) => {
  const [form] = Form.useForm<{
    instructor: boolean;
    admin_tier: StaffAdminTier;
    password?: string;
  }>();
  const initialInstructor = hasRole(user.roles, UserType.Instructor);
  const initialTier = tierOf(user.roles);
  const instructor = Form.useWatch("instructor", form) ?? initialInstructor;
  const tier: StaffAdminTier = Form.useWatch("admin_tier", form) ?? initialTier;
  const { mutate, isPending } = useSetStaffRoles();
  const queryClient = useQueryClient();

  // Re-seed after every save (the mutation invalidates ["user", id] and the
  // parent re-renders with fresh roles); Form initialValues only apply once.
  useEffect(() => {
    form.setFieldsValue({
      instructor: initialInstructor,
      admin_tier: initialTier,
      password: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const grantsAdminTier = tier !== "none" && initialTier === "none";
  // Mirrors the server's two admin-grant refusals so the sysadmin is told
  // before the request rather than by a 422 toast. Any country is fine for an
  // admin number, but there has to be one: a non-teaching Staf may have none.
  const needsPassword = grantsAdminTier && !user.has_password;
  const phoneBlocks = grantsAdminTier && !user.phone_number;
  const empty = !instructor && tier === "none";
  const dirty = instructor !== initialInstructor || tier !== initialTier;
  const canSave = dirty && !empty && !phoneBlocks && !isPending;

  const onSave = () => {
    form
      .validateFields()
      .then(async (values) => {
        const body: SetStaffRolesBody = {
          instructor: values.instructor,
          admin_tier: values.admin_tier,
          ...(needsPassword && values.password ? { password: values.password } : {}),
        };
        // Taking the instructor role away also deletes their upcoming
        // classes (the server refuses it otherwise). A system admin can't see
        // or move a studio's classes, so the confirm states how many go
        // instead of listing them (Andrew, 2026-09-26).
        let removal: { classes: number; bookings: number } | null = null;
        if (!body.instructor && initialInstructor) {
          const { queryKey, queryFn } = staffDeactivationCheckOptions(user.id, true);
          let check;
          try {
            check = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
          } catch (e) {
            getApiErrorMessage(e as AxiosError<ResponseHandler>);
            return;
          }
          if (check.totals.classes > 0) {
            if (!check.can_remove_all) {
              message.error(translate("messages.error.too_many_upcoming_classes"));
              return;
            }
            removal = check.totals;
            body.remove_upcoming_classes = true;
          }
        }
        const lines: string[] = [];
        if (body.instructor && !initialInstructor) {
          lines.push(translate("staff_roles.confirm_grant_instructor"));
        }
        if (!body.instructor && initialInstructor) {
          lines.push(translate("staff_roles.confirm_revoke_instructor"));
          if (removal) {
            lines.push(
              translateWithVars(
                removal.bookings > 0
                  ? "staff_status.summary.body_bookings"
                  : "staff_status.summary.body",
                removalCountLabels(removal.classes, removal.bookings)
              )
            );
          }
        }
        if (body.admin_tier !== initialTier) {
          lines.push(
            translateWithVars("staff_roles.confirm_tier", {
              from: tierLabel(initialTier),
              to: tierLabel(body.admin_tier),
            })
          );
        }
        if (grantsAdminTier) {
          // The one rule that changes for the studio: a plain Admin who could
          // deactivate this instructor yesterday finds the button greyed out.
          lines.push(translate("staff_roles.confirm_admin_rule"));
          if (!user.is_verified) lines.push(translate("staff_roles.confirm_otp"));
        }
        // Only a TIER change ends the person's dashboard session (the admin
        // gates refuse a token minted for another tier); an instructor-only
        // change leaves it alone, like the Manager's toggle.
        if (body.admin_tier !== initialTier) {
          lines.push(translate("staff_roles.confirm_signout"));
        }
        showConfirmModal({
          tone: "amber",
          title: translate("staff_roles.confirm_title"),
          content: (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: colors.ink2 }}>
              {lines.map((line) => (
                <li key={line} style={{ marginBottom: 6 }}>
                  {line}
                </li>
              ))}
            </ul>
          ),
          confirmLabel: translate("staff_roles.confirm_button"),
          destructive: !!removal,
          onConfirm: () => mutate({ userId: user.id, body }),
        });
      })
      .catch(() => {
        // AntD already paints the inline password error.
      });
  };

  return (
    <Card>
      <Flex vertical gap={20}>
        <CardSectionTitle
          title={translate("staff_roles.title")}
          subtitle={translate("staff_roles.subtitle")}
        />
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ instructor: initialInstructor, admin_tier: initialTier }}
        >
          <div style={optionBlockStyle(false)}>
            <ToggleRow
              name="instructor"
              labelKey="staff_roles.instructor_label"
              descKey="staff_roles.instructor_desc"
            />
          </div>
          <div style={optionBlockStyle(true)}>
            <Flex justify="space-between" align="center" gap={16}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: textPx(14.5) }}>
                  {translate("staff_roles.tier_label")}
                </div>
                <Text type="secondary" style={{ fontSize: textPx(12.5), display: "block", marginTop: 2 }}>
                  {translate("staff_roles.tier_desc")}
                </Text>
              </div>
              <Form.Item name="admin_tier" noStyle>
                <Select
                  style={{ width: 160, flexShrink: 0 }}
                  aria-label={translate("staff_roles.tier_label")}
                  options={(["none", "admin", "manager"] as StaffAdminTier[]).map((value) => ({
                    value,
                    label: tierLabel(value),
                  }))}
                />
              </Form.Item>
            </Flex>
            {phoneBlocks && (
              <Text type="danger" style={{ fontSize: textPx(12.5), display: "block", marginTop: 10 }}>
                {translate("staff_roles.phone_required")}
              </Text>
            )}
            {needsPassword && !phoneBlocks && (
              <Form.Item
                name="password"
                label={translate("staff_roles.password_label")}
                extra={translate("staff_roles.password_hint")}
                style={{ marginTop: 14, marginBottom: 0, maxWidth: 360 }}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.input")} ${translate(
                      "label.password"
                    )} ${translate("form.error.input_end")}`,
                  },
                  { min: 6, message: translate("form.error.password") },
                ]}
              >
                <Input.Password placeholder="••••••••" autoComplete="new-password" />
              </Form.Item>
            )}
            {empty && (
              <Text type="danger" style={{ fontSize: textPx(12.5), display: "block", marginTop: 10 }}>
                {translate("staff_roles.empty_hint")}
              </Text>
            )}
          </div>
          <Flex style={{ marginTop: 18 }}>
            <Button type="primary" disabled={!canSave} loading={isPending} onClick={onSave}>
              {translate("button.save_changes")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Card>
  );
};

export default StaffRolesCard;
